import React, {useState, useEffect} from "react";
import {Eye, EyeOff, GripVertical, RotateCcw, Check, SlidersHorizontal, X} from "lucide-react";
import {useTranslation} from "react-i18next";
import {KPI_DEFINITIONS, CHART_DEFINITIONS, KPI_BY_ID} from "./widgetCatalog";

// One toggleable row in the "all available widgets" lists.
const ToggleRow = ({def, isVisible, onToggle, isDarkMode}) => {
    const {t} = useTranslation("activite");
    const Icon = def.icon;
    return (
        <button
            type="button"
            onClick={() => onToggle(def.id)}
            aria-pressed={isVisible}
            className={`flex items-center justify-between gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors ${
                isVisible
                    ? "border-primary/40 bg-primary/5"
                    : isDarkMode
                        ? "border-gray-700 bg-gray-800 hover:bg-gray-700/60"
                        : "border-gray-200 bg-white hover:bg-gray-50"
            }`}
        >
            <span className="flex min-w-0 items-center gap-2">
                {Icon && (
                    <Icon
                        className={`h-4 w-4 shrink-0 ${isVisible ? "text-primary" : isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                        strokeWidth={2}
                    />
                )}
                <span
                    className={`truncate text-xs font-medium ${
                        isDarkMode ? "text-gray-200" : "text-gray-700"
                    }`}
                >
                    {t(def.labelKey)}
                </span>
            </span>
            {isVisible ? (
                <Eye className="h-4 w-4 shrink-0 text-primary" />
            ) : (
                <EyeOff className={`h-4 w-4 shrink-0 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`} />
            )}
        </button>
    );
};

/**
 * Modal configuration panel. Lets the user show/hide every KPI and chart, and
 * drag-reorder the visible KPI cards. Preferences persist per account.
 */
const KpiSelectorPanel = ({prefs, isDarkMode, onClose}) => {
    const {t} = useTranslation("activite");
    const {
        visibleKpis,
        isKpiVisible,
        isChartVisible,
        toggleKpi,
        toggleChart,
        reorderKpis,
        resetAll,
    } = prefs;

    const [dragIndex, setDragIndex] = useState(null);
    const [overIndex, setOverIndex] = useState(null);

    // Close on Escape + lock background scroll while open.
    useEffect(() => {
        const handleKey = (e) => {
            if (e.key === "Escape") onClose();
        };
        document.addEventListener("keydown", handleKey);
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.removeEventListener("keydown", handleKey);
            document.body.style.overflow = prevOverflow;
        };
    }, [onClose]);

    const handleDrop = (toIndex) => {
        if (dragIndex !== null && dragIndex !== toIndex) {
            reorderKpis(dragIndex, toIndex);
        }
        setDragIndex(null);
        setOverIndex(null);
    };

    const sectionTitle = (text) => (
        <h4
            className={`mb-2 text-xs font-semibold uppercase tracking-wide ${
                isDarkMode ? "text-gray-400" : "text-gray-500"
            }`}
        >
            {text}
        </h4>
    );

    return (
        <div
            className="fixed inset-0 z-50 flex items-end justify-center p-0 sm:items-center sm:p-4"
            onClick={(e) => {
                if (e.target === e.currentTarget) onClose();
            }}
        >
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

            {/* Modal */}
            <div
                role="dialog"
                aria-modal="true"
                aria-label={t("dashboard.customizeDashboard")}
                className={`relative flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-t-2xl shadow-2xl sm:rounded-2xl ${
                    isDarkMode ? "bg-gray-800 text-gray-100" : "bg-white text-gray-900"
                }`}
            >
                {/* Header */}
                <div className="flex items-center justify-between border-b border-primary/30 bg-primary/10 px-5 py-4">
                    <div className="flex items-center gap-2">
                        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary">
                            <SlidersHorizontal className="h-4 w-4 text-white" />
                        </div>
                        <div>
                            <h3 className="text-sm font-semibold text-primary">
                                {t("dashboard.customizeDashboard")}
                            </h3>
                            <p className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                                {t("dashboard.customizeHint")}
                            </p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className={`rounded-full p-1.5 transition-colors ${
                            isDarkMode ? "text-gray-400 hover:bg-gray-700" : "text-gray-500 hover:bg-gray-100"
                        }`}
                        aria-label={t("dashboard.close")}
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>

                {/* Body (scrollable) */}
                <div className="flex-1 overflow-y-auto p-5">
                    {/* Reorder visible KPI cards */}
                    {visibleKpis.length > 0 && (
                        <div className="mb-5">
                            {sectionTitle(t("dashboard.kpiCardsOrder"))}
                            <ul className="flex flex-col gap-1.5">
                                {visibleKpis.map((id, index) => {
                                    const def = KPI_BY_ID[id];
                                    if (!def) return null;
                                    const Icon = def.icon;
                                    return (
                                        <li
                                            key={id}
                                            draggable
                                            onDragStart={() => setDragIndex(index)}
                                            onDragOver={(e) => {
                                                e.preventDefault();
                                                setOverIndex(index);
                                            }}
                                            onDrop={() => handleDrop(index)}
                                            onDragEnd={() => {
                                                setDragIndex(null);
                                                setOverIndex(null);
                                            }}
                                            className={`flex cursor-grab items-center gap-2 rounded-lg border px-3 py-2 text-xs transition-colors active:cursor-grabbing ${
                                                overIndex === index && dragIndex !== index
                                                    ? "border-dashed border-primary"
                                                    : isDarkMode
                                                        ? "border-gray-700 bg-gray-800"
                                                        : "border-gray-200 bg-white"
                                            } ${dragIndex === index ? "opacity-50" : ""}`}
                                        >
                                            <GripVertical
                                                className={`h-4 w-4 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                                            />
                                            {Icon && <Icon className="h-4 w-4 text-primary" strokeWidth={2} />}
                                            <span className={isDarkMode ? "text-gray-200" : "text-gray-700"}>
                                                {t(def.labelKey)}
                                            </span>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    )}

                    {/* Toggle KPIs */}
                    <div className="mb-5">
                        {sectionTitle(t("dashboard.kpisSection"))}
                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                            {KPI_DEFINITIONS.map((def) => (
                                <ToggleRow
                                    key={def.id}
                                    def={def}
                                    isVisible={isKpiVisible(def.id)}
                                    onToggle={toggleKpi}
                                    isDarkMode={isDarkMode}
                                />
                            ))}
                        </div>
                    </div>

                    {/* Toggle charts */}
                    <div>
                        {sectionTitle(t("dashboard.chartsSection"))}
                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                            {CHART_DEFINITIONS.map((def) => (
                                <ToggleRow
                                    key={def.id}
                                    def={def}
                                    isVisible={isChartVisible(def.id)}
                                    onToggle={toggleChart}
                                    isDarkMode={isDarkMode}
                                />
                            ))}
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div
                    className={`flex items-center justify-between gap-3 border-t px-5 py-3 ${
                        isDarkMode ? "border-gray-700 bg-gray-800" : "border-gray-200 bg-gray-50"
                    }`}
                >
                    <span
                        className={`flex items-center gap-1.5 text-xs ${
                            isDarkMode ? "text-gray-500" : "text-gray-400"
                        }`}
                    >
                        <Check className="h-3.5 w-3.5 text-primary" />
                        {t("dashboard.autoSaved")}
                    </span>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={resetAll}
                            className={`flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors ${
                                isDarkMode
                                    ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                                    : "border-gray-300 text-gray-600 hover:bg-gray-100"
                            }`}
                        >
                            <RotateCcw className="h-3.5 w-3.5" />
                            {t("dashboard.reset")}
                        </button>
                        <button
                            type="button"
                            onClick={onClose}
                            className="rounded-lg bg-primary px-4 py-1.5 text-xs font-medium text-white transition-opacity hover:opacity-90"
                        >
                            {t("dashboard.done")}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default KpiSelectorPanel;
